import type { NoticeLayer } from '@cornerstone/shared';
export type LayerUnit = { id: string; groupId?: string; name?: string; layers: NoticeLayer[] };
export function noticeLayerUnits(layers: readonly NoticeLayer[]): LayerUnit[] {
  const sorted=[...layers].sort((a,b)=>b.z-a.z), seen=new Set<string>(), units:LayerUnit[]=[];
  for(const layer of sorted){
    const group=layer.editorGroup;
    if(!group){units.push({id:layer.id,layers:[layer]});continue;}
    if(seen.has(group.id))continue;seen.add(group.id);
    units.push({id:`group:${group.id}`,groupId:group.id,name:group.name,layers:sorted.filter(item=>item.editorGroup?.id===group.id)});
  }
  return units;
}
export function expandNoticeGroupIds(layers: readonly NoticeLayer[], ids: readonly string[]): string[] {
  const groups=new Set(layers.filter(layer=>ids.includes(layer.id)).flatMap(layer=>layer.editorGroup?[layer.editorGroup.id]:[]));
  return layers.filter(layer=>ids.includes(layer.id)||!!layer.editorGroup&&groups.has(layer.editorGroup.id)).map(layer=>layer.id);
}
export function lockedNoticeIds(layers: readonly NoticeLayer[]): Set<string> {
  return new Set(expandNoticeGroupIds(layers,layers.filter(layer=>layer.locked).map(layer=>layer.id)));
}
const withoutGroup=(layer:NoticeLayer):NoticeLayer=>{const {editorGroup:_,...rest}=layer;return rest as NoticeLayer;};
export function groupNoticeLayers(layers: NoticeLayer[], ids: string[], name='그룹'): NoticeLayer[] {
  const locked=lockedNoticeIds(layers), selected=layers.filter(layer=>ids.includes(layer.id));
  if(selected.length<2||selected.some(layer=>locked.has(layer.id)))return layers;
  const group={id:`group-${crypto.randomUUID()}`,name};
  const sorted=[...layers].sort((a,b)=>b.z-a.z), top=sorted.findIndex(layer=>ids.includes(layer.id));
  const rest=sorted.filter(layer=>!ids.includes(layer.id));
  rest.splice(top,0,...sorted.filter(layer=>ids.includes(layer.id)).map(layer=>({...layer,editorGroup:group})));
  return rest.reverse().map((layer,z)=>({...layer,z}));
}
export function ungroupNoticeLayers(layers: NoticeLayer[], ids: string[]): NoticeLayer[] {
  const locked=lockedNoticeIds(layers);
  const groups=new Set(layers.filter(layer=>ids.includes(layer.id)&&!locked.has(layer.id)).flatMap(layer=>layer.editorGroup?[layer.editorGroup.id]:[]));
  if(!groups.size)return layers;
  return layers.map(layer=>layer.editorGroup&&groups.has(layer.editorGroup.id)?withoutGroup(layer):layer);
}
/** One drop produces one history entry. Group headers move as an intact block. */
export function dropNoticeLayers(layers:NoticeLayer[],ids:string[],targetIds:string[],after:boolean,intoGroup:string|null=null):NoticeLayer[]{
  const locked=lockedNoticeIds(layers);if(!ids.length||ids.some(id=>locked.has(id))||targetIds.some(id=>ids.includes(id)))return layers;
  const sorted=[...layers].sort((a,b)=>b.z-a.z), moving=sorted.filter(layer=>ids.includes(layer.id));
  if(!moving.length)return layers;
  const group=intoGroup?layers.find(layer=>layer.editorGroup?.id===intoGroup)?.editorGroup:null;
  if(group&&layers.some(layer=>layer.editorGroup?.id===group.id&&locked.has(layer.id)))return layers;
  const completeGroups=new Set(moving.flatMap(layer=>layer.editorGroup&&layers.filter(l=>l.editorGroup?.id===layer.editorGroup!.id).every(l=>ids.includes(l.id))?[layer.editorGroup.id]:[]));
  const changed=moving.map(layer=>group?{...layer,editorGroup:group}:layer.editorGroup&&!completeGroups.has(layer.editorGroup.id)?withoutGroup(layer):layer);
  const rest=sorted.filter(layer=>!ids.includes(layer.id)), indices=rest.flatMap((layer,index)=>targetIds.includes(layer.id)?[index]:[]);
  if(!indices.length)return layers;
  rest.splice(after?Math.max(...indices)+1:Math.min(...indices),0,...changed);
  if(rest.every((layer,index)=>layer.id===sorted[index]!.id&&layer.editorGroup?.id===sorted[index]!.editorGroup?.id))return layers;
  return rest.reverse().map((layer,z)=>({...layer,z}));
}
